import { useNavigate } from "react-router-dom";
import { Check, ChevronsUpDown, Crown, Plus, Settings2 } from "lucide-react";
import { useUserRole } from "@/hooks/useUserRole";
import { useWorkspace } from "@/hooks/useWorkspace";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

type PersonaSwitcherProps = {
  collapsed?: boolean;
};

export const PersonaSwitcher = ({ collapsed = false }: PersonaSwitcherProps) => {
  const { isSuperAdmin } = useUserRole();
  const { allOwnedWorkspaces, active, setActive } = useWorkspace();
  const navigate = useNavigate();

  if (!isSuperAdmin) return null;

  const personas = allOwnedWorkspaces.filter((w) => w.is_primary);
  const activeLabel = active?.display_name || active?.workspace_label || "Untitled";

  const trigger = collapsed ? (
    <Button
      variant="ghost"
      size="icon"
      className="h-9 w-9 mx-auto"
      aria-label={`Switch persona (current: ${activeLabel})`}
    >
      <Crown className="h-4 w-4 text-primary" />
    </Button>
  ) : (
    <Button
      variant="ghost"
      size="sm"
      className="w-full justify-start gap-2 px-3 py-2 h-auto font-medium text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-foreground focus-visible:text-sidebar-foreground data-[state=open]:text-sidebar-foreground"
    >
      <Crown className="h-4 w-4 text-primary shrink-0" />
      <span className="flex-1 min-w-0 text-left">
        <span className="block truncate text-sm">{activeLabel}</span>
        {active?.account_tier && active.account_tier !== "super_admin" && (
          <span className="block truncate text-[10px] uppercase tracking-wide text-muted-foreground">
            {active.account_tier}
          </span>
        )}
      </span>
      <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
    </Button>
  );

  return (
    <DropdownMenu>
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="right">Persona: {activeLabel}</TooltipContent>
        </Tooltip>
      ) : (
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      )}
      <DropdownMenuContent align="start" className="w-60">
        <DropdownMenuLabel className="text-xs text-muted-foreground">Switch Persona</DropdownMenuLabel>
        {personas.length === 0 && (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">No Personas yet.</div>
        )}
        {personas.map((p) => {
          const label = p.display_name || p.workspace_label || "Untitled";
          const isActive = active?.id === p.id;
          return (
            <DropdownMenuItem
              key={p.id}
              onSelect={() => setActive(p.id)}
              className="flex items-center gap-2"
            >
              <Check className={isActive ? "h-3.5 w-3.5 opacity-100" : "h-3.5 w-3.5 opacity-0"} />
              <span className="flex-1 truncate">{label}</span>
              {p.account_tier !== "super_admin" && (
                <Badge variant="secondary" className="text-[10px] py-0">{p.account_tier}</Badge>
              )}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate("/super-admin?tab=my-workspaces")}>
          <Settings2 className="h-3.5 w-3.5" /> Manage Personas
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate("/super-admin?tab=my-workspaces")}>
          <Plus className="h-3.5 w-3.5" /> New Persona
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
